import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  X,
  Search,
  ChevronLeft,
  ChevronRight,
  Eye,
  FileText,
  Download,
  Users,
  Building2,
  Award,
  Calendar,
  Filter,
  ExternalLink,
} from "lucide-react";
import { BASE_URL } from "../../../services/configUrls";

// ─── Nomination Detail Modal ───────────────────────────────────────────────
const NominationDetailModal = ({ isOpen, onClose, nomination }) => {
  if (!isOpen || !nomination) return null;

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      style={{ zIndex: 9999 }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-purple-50 shrink-0">
          <div>
            <h2 className="text-xl font-extrabold text-gray-800">
              Nomination Details
            </h2>
            <p className="text-sm text-gray-500 mt-1 font-medium">
              {nomination.award_name || "—"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-white rounded-xl"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">


          {/* Questions & Answers */}
          <h3 className="text-base font-extrabold text-gray-800 mb-4 flex items-center">
            <FileText className="w-4 h-4 mr-2 text-indigo-500" />
            Questions & Answers
          </h3>

          {nomination.answers && nomination.answers.length > 0 ? (
            <div className="space-y-4">
              {nomination.answers.map((a, idx) => (
                <div
                  key={idx}
                  className="bg-white border border-gray-200 rounded-xl p-5 hover:border-indigo-200 transition-colors"
                >
                  <div className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-2">
                    Question {idx + 1}
                  </div>
                  <p className="text-sm font-semibold text-gray-800 mb-3">
                    {a.question_text || "—"}
                  </p>

                  {a.answer && (
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                      <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                        {a.answer}
                      </p>
                    </div>
                  )}

                  {a.file_path && (
                    <div className="mt-3">
                      <a
                        href={
                          a.file_path.startsWith("http")
                            ? a.file_path
                            : `${BASE_URL}/${a.file_path}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-xs font-bold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 px-3 py-2 rounded-lg transition-colors border border-indigo-100"
                      >
                        <Download className="w-3.5 h-3.5 mr-1.5" />
                        {a.file_name || "View Attachment"}
                        <ExternalLink className="w-3 h-3 ml-1.5 opacity-50" />
                      </a>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-gray-50 rounded-xl p-8 text-center border border-gray-100">
              <p className="text-sm text-gray-500 font-medium">
                No answers submitted yet.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

// ─── Nominees Dashboard Component ──────────────────────────────────────────
const NomineesDashboard = ({ apiCall }) => {
  const [nominations, setNominations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 20,
    total: 0,
    total_pages: 0,
  });
  const [filters, setFilters] = useState({
    award_id: "",
    institute_id: "",
  });
  const [dropdowns, setDropdowns] = useState({
    institutes: [],
    awards: [],
  });
  const [selectedNomination, setSelectedNomination] = useState(null);

  // Fetch filter dropdowns on mount
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const data = await apiCall("/awards/nominations/filter-dropdowns");
        if (data) {
          setDropdowns({
            institutes: data.institutes || [],
            awards: data.awards || [],
          });
        }
      } catch (error) {
        console.error("Failed to fetch filter dropdowns:", error);
      }
    };
    fetchDropdowns();
  }, []);

  // Fetch nominations on filter/page change
  useEffect(() => {
    fetchNominations();
  }, [filters, pagination.page]);

  const fetchNominations = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.award_id) params.append("award_id", filters.award_id);
      if (filters.institute_id)
        params.append("institute_id", filters.institute_id);
      params.append("page", pagination.page);
      params.append("page_size", pagination.page_size);

      const data = await apiCall(
        `/awards/nominations/all?${params.toString()}`
      );
      if (data) {
        setNominations(data.nominations || []);
        setPagination((prev) => ({
          ...prev,
          total: data.total || 0,
          total_pages: data.total_pages || 0,
        }));
      }
    } catch (error) {
      console.error("Failed to fetch nominations:", error);
      setNominations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({ award_id: "", institute_id: "" });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const hasActiveFilters = filters.award_id || filters.institute_id;

  return (
    <div className="space-y-6">
      {/* Filter Bar */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <h2 className="text-2xl font-bold text-gray-800">
          Nominations
          {pagination.total > 0 && (
            <span className="text-sm font-medium text-gray-400 ml-3">
              ({pagination.total} total)
            </span>
          )}
        </h2>

        <div className="bg-white p-3 rounded-2xl shadow-sm border border-gray-100 inline-flex flex-wrap gap-3 items-center w-full md:w-auto justify-end">
          <div className="flex items-center text-gray-400 mr-1">
            <Filter className="w-4 h-4" />
          </div>

          <select
            value={filters.award_id}
            onChange={(e) => handleFilterChange("award_id", e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm text-gray-700 bg-gray-50/50 min-w-[160px]"
          >
            <option value="">All Awards</option>
            {dropdowns.awards.map((a) => (
              <option key={a.award_id} value={a.award_id}>
                {a.award_name}
              </option>
            ))}
          </select>

          <select
            value={filters.institute_id}
            onChange={(e) => handleFilterChange("institute_id", e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm text-gray-700 bg-gray-50/50 min-w-[160px]"
          >
            <option value="">All Institutes</option>
            {dropdowns.institutes.map((inst) => (
              <option key={inst.institute_id} value={inst.institute_id}>
                {inst.institute_name}
              </option>
            ))}
          </select>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="px-3 py-2 text-sm text-red-500 hover:text-red-700 font-bold transition-colors"
            >
              Clear All
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white/50 rounded-2xl border border-white">
          <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mb-4"></div>
          <p className="text-gray-500 font-medium">Loading nominations...</p>
        </div>
      ) : nominations.length > 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-gray-50 border-b border-gray-100">
            <div className="col-span-3 text-xs font-bold text-gray-500 uppercase tracking-wider">
              Email
            </div>
            <div className="col-span-3 text-xs font-bold text-gray-500 uppercase tracking-wider">
              Institute
            </div>
            <div className="col-span-3 text-xs font-bold text-gray-500 uppercase tracking-wider">
              Award
            </div>
            <div className="col-span-2 text-xs font-bold text-gray-500 uppercase tracking-wider">
              Submitted
            </div>
            <div className="col-span-1 text-xs font-bold text-gray-500 uppercase tracking-wider text-right">
              Action
            </div>
          </div>

          {/* Table Body */}
          {nominations.map((nom, idx) => (
            <div
              key={idx}
              className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-gray-50 hover:bg-indigo-50/30 transition-colors items-center group"
            >
              <div className="col-span-3 text-sm text-gray-700 font-medium truncate" title={nom.user_email}>
                {nom.user_email || "—"}
              </div>
              <div className="col-span-3 text-sm text-gray-700 font-medium truncate" title={nom.institute_name}>
                {nom.institute_name || "—"}
              </div>
              <div className="col-span-3">
                <span className="text-sm text-gray-700 font-medium truncate block" title={nom.award_name}>
                  {nom.award_name || "—"}
                </span>
              </div>
              <div className="col-span-2 text-sm text-gray-500 font-medium">
                {nom.submitted_at ? nom.submitted_at : "—"}
              </div>
              <div className="col-span-1 text-right">
                <button
                  onClick={() => setSelectedNomination(nom)}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-bold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors border border-indigo-100 group-hover:bg-indigo-100"
                >
                  <Eye className="w-3.5 h-3.5 mr-1" />
                  View
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm p-16 text-center border border-gray-100 flex flex-col items-center justify-center">
          <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
            <Users className="w-8 h-8 text-gray-300" />
          </div>
          <h4 className="text-gray-800 font-bold text-lg mb-2">
            No Nominations Found
          </h4>
          <p className="text-gray-500 text-sm max-w-xs mx-auto">
            {hasActiveFilters
              ? "No nominations match the selected filters. Try adjusting your filters."
              : "There are no nominations submitted yet."}
          </p>
        </div>
      )}

      {/* Pagination */}
      {!loading && nominations.length > 0 && pagination.total_pages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <div className="text-sm text-gray-600 font-medium">
            Showing{" "}
            <span className="font-bold text-gray-900">
              {(pagination.page - 1) * pagination.page_size + 1}
            </span>{" "}
            to{" "}
            <span className="font-bold text-gray-900">
              {Math.min(
                pagination.page * pagination.page_size,
                pagination.total
              )}
            </span>{" "}
            of{" "}
            <span className="font-bold text-gray-900">{pagination.total}</span>{" "}
            nominations
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() =>
                setPagination((prev) => ({
                  ...prev,
                  page: Math.max(prev.page - 1, 1),
                }))
              }
              disabled={pagination.page === 1}
              className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={16} />
            </button>

            <div className="flex space-x-1">
              {[...Array(pagination.total_pages)].map((_, i) => {
                const pageNum = i + 1;
                if (
                  pageNum === 1 ||
                  pageNum === pagination.total_pages ||
                  Math.abs(pagination.page - pageNum) <= 1
                ) {
                  return (
                    <button
                      key={pageNum}
                      onClick={() =>
                        setPagination((prev) => ({ ...prev, page: pageNum }))
                      }
                      className={`px-3.5 py-1.5 rounded-lg text-sm font-bold transition-colors ${
                        pagination.page === pageNum
                          ? "bg-indigo-600 text-white shadow-md"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                } else if (Math.abs(pagination.page - pageNum) === 2) {
                  return (
                    <span
                      key={`dots-${pageNum}`}
                      className="px-2 text-gray-400"
                    >
                      ...
                    </span>
                  );
                }
                return null;
              })}
            </div>

            <button
              onClick={() =>
                setPagination((prev) => ({
                  ...prev,
                  page: Math.min(prev.page + 1, pagination.total_pages),
                }))
              }
              disabled={pagination.page === pagination.total_pages}
              className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      <NominationDetailModal
        isOpen={!!selectedNomination}
        onClose={() => setSelectedNomination(null)}
        nomination={selectedNomination}
      />
    </div>
  );
};

export default NomineesDashboard;
